/**
 * The atlas palette, transcribed from the ZAIRAI reference field.
 *
 * Two complete palettes rather than tokens, and deliberately not the
 * app's `theme.css` variables: the sky is a drawing, not chrome, and its
 * colours have to be readable as numbers by the SVG helpers (mixing,
 * alpha suffixes, gradient stops) rather than as CSS custom properties a
 * `<path fill>` cannot interpolate.
 */

export type Palette = {
  bg: string;
  panel: string;
  panel2: string;
  line: string;
  text: string;
  dim: string;
  faint: string;
  gold: string;
  goldHi: string;
  spring: string;
  sgold: string;
  russet: string;
  outline: string;
  bgIn: string;
  bgOut: string;
  /** Ring accents, cycled when an atlas has more rings than colours. */
  rings: string[];
  inset: string;
  night: boolean;
  /** The rung scale — dormant · familiar · mechanistic · integrated. */
  lv: string[];
};

export const LIGHT: Palette = {
  bg: "#F5EDDA", panel: "#F0E5CD", panel2: "#E9DCBE", line: "#C6B78F",
  text: "#2A2317", dim: "#6B5F4B", faint: "#98896C", gold: "#A87A1F",
  goldHi: "#C6952B", spring: "#4C9C5C", sgold: "#C08A28", russet: "#A65A30",
  outline: "#4A3F28", bgIn: "#F8F1E0", bgOut: "#EADCBB",
  rings: ["#5E9A67", "#5B87B8", "#C08A3E", "#9B6FBF"],
  inset: "inset 0 0 60px rgba(122,96,42,0.12)", night: false,
  lv: ["#A2937A", "#2E7C86", "#B07A16", "#7A4396"],
};

export const DARK: Palette = {
  bg: "#0A0E1E", panel: "#10162B", panel2: "#161E38", line: "#2A3556",
  text: "#E8ECF8", dim: "#97A1C0", faint: "#5B6688", gold: "#F2D06B",
  goldHi: "#FFDF85", spring: "#6FCB84", sgold: "#E5B54D", russet: "#C97B54",
  outline: "#050810", bgIn: "#141C38", bgOut: "#070B17",
  rings: ["#7FC98A", "#7FA8E8", "#E8B45C", "#C79AE8"],
  inset: "inset 0 0 70px rgba(0,0,0,0.55)", night: true,
  lv: ["#4A5573", "#5AD2DE", "#F5C25A", "#C79BFF"],
};

/** The two-crossing gauge colours: proficient arc, then mastery ring. */
export const VERDIGRIS = { l: ["#12796B", "#A8760F"], d: ["#3FD8B0", "#FFC64A"] };

export const gaugeColours = (P: Palette) => (P.night ? VERDIGRIS.d : VERDIGRIS.l);

export const ringColour = (P: Palette, level: number) =>
  P.rings[((level % P.rings.length) + P.rings.length) % P.rings.length];

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
export const SERIF = "Georgia, 'Iowan Old Style', 'Times New Roman', serif";

// ── colour arithmetic ────────────────────────────────────────────────

const hexc = (h: string): [number, number, number] => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/**
 * Blend two hex colours, and give back HEX.
 *
 * Not `rgb(...)`: the result is pasted into strings that append a
 * two-digit alpha (`${colour}77` on a border, `${colour}88` on a
 * box-shadow), and a function-notation colour makes those declarations
 * invalid — which CSS drops whole and silently, so the border simply
 * disappears and nothing says why.
 */
export const mix = (h1: string, h2: string, t: number) => {
  const a = hexc(h1);
  const b = hexc(h2);
  return `#${[0, 1, 2]
    .map((i) => Math.round(lerp(a[i], b[i], t)).toString(16).padStart(2, "0"))
    .join("")}`;
};

const ease = (r: number) => Math.pow(clamp01(r), 0.6);

/**
 * The season colour: how warm a world reads.
 *
 * The input is a WORD, not a score.  Grip's scalar is ops-only by the
 * audience law and never crosses the wire, so the server sends the band
 * and the client picks the paint — which is also the right split, since
 * the palette is a day/night decision the server has no business making.
 *
 * `null` means nothing here has been measured, and it gets the neutral
 * spring rather than the russet of something that has decayed: "we have
 * never asked" and "you have forgotten it" are different facts and the
 * map must not conflate them.
 */
export const seasonColour = (P: Palette, heat: "FRESH" | "WARM" | "FADING" | "COLD" | null) => {
  switch (heat) {
    case "FRESH":
      return P.spring;
    case "WARM":
      return mix(P.sgold, P.spring, ease(0.55));
    case "FADING":
      return mix(P.russet, P.sgold, 0.5);
    case "COLD":
      return P.russet;
    default:
      // Unmeasured, and it must not be the same paint as FRESH: a
      // learner would read "held" off ground nobody has asked them
      // about.  Neutral, off the warm/cool axis entirely, so it reads
      // as absence rather than as a reading.
      return P.faint;
  }
};


/**
 * A track's colour, derived from its id.
 *
 * The reference field hand-picked one per charter; the track vocabulary
 * is closed but not this file's to know, so the hue comes from the same
 * hash the planets use.  Five well-separated hues fall out, and a sixth
 * track added in code would get its own without anybody choosing it.
 */
export function trackColour(P: Palette, trackId: string, hash: (text: string) => number) {
  const hue = hash(`track:${trackId}`) % 360;
  // Through the same HSL→hex helper the planets use, for the same
  // reason: these colours get an alpha suffix appended downstream.
  return hslToHex(hue, P.night ? 62 : 52, P.night ? 72 : 38);
}

/** HSL → ``#rrggbb``.  See ``mix`` for why the output must be hex. */
export function hslToHex(h: number, s: number, l: number): string {
  const hue = (((h % 360) + 360) % 360) / 360;
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;
  const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat;
  const p = 2 * light - q;
  const channel = (t: number) => {
    let value = t;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    if (value < 1 / 6) return p + (q - p) * 6 * value;
    if (value < 1 / 2) return q;
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
    return p;
  };
  const parts = sat === 0 ? [light, light, light] : [channel(hue + 1 / 3), channel(hue), channel(hue - 1 / 3)];
  return `#${parts.map((v) => Math.round(v * 255).toString(16).padStart(2, "0")).join("")}`;
}


// ── the families ─────────────────────────────────────────────────────
//
// The column tones — muted, and legible against both grounds.  Keyed by
// sector id; a family the palette has not met takes a hue from the same
// hash the planets use, so a sixth family added in code is not grey.

const SECTOR_HUES: Record<string, [string, string]> = {
  shared: ["#8C8168", "#C2B79C"],
  pred: ["#4A72A6", "#87A8D6"],
  den: ["#2F8479", "#6FBCAF"],
  shape: ["#B87536", "#DFA267"],
  stack: ["#7359A0", "#A794CE"],
};

export function sectorColour(P: Palette, sectorId: string, hash: (text: string) => number) {
  const known = SECTOR_HUES[sectorId];
  if (known) return known[P.night ? 1 : 0];
  return hslToHex(hash(`sector:${sectorId}`) % 360, P.night ? 40 : 34, P.night ? 66 : 42);
}

/** The weave: every other column takes a wash of one of two tints. */
const WEAVE: [string, string][] = [
  ["#B98A5E", "#C69A6C"],
  ["#5F87A6", "#7FA6C4"],
];

export const weaveColour = (P: Palette, index: number) => WEAVE[index % 2][P.night ? 1 : 0];
